const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Generic fetch wrapper
async function apiFetch(endpoint, options = {}) {
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erro de rede' }));
    throw new Error(error.detail || 'Erro ao processar pedido');
  }

  return response.json();
}

// Auth API
export const authAPI = {
  exchangeSession: (sessionId) => 
    apiFetch('/auth/session', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    }),
  
  getMe: () => apiFetch('/auth/me'),
  
  updateMe: (data) => 
    apiFetch('/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  logout: () => apiFetch('/auth/logout', { method: 'POST' }),
};

// Cities & Service Types API
export const configAPI = {
  getCities: () => apiFetch('/cities'),
  getServiceTypes: () => apiFetch('/service-types'),
};

// Pets API
export const petsAPI = {
  getMyPets: () => apiFetch('/pets'),
  
  createPet: (data) => 
    apiFetch('/pets', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updatePet: (petId, data) => 
    apiFetch(`/pets/${petId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  deletePet: (petId) => 
    apiFetch(`/pets/${petId}`, { method: 'DELETE' }),
};

// Providers API
export const providersAPI = {
  getProviders: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiFetch(`/providers${query ? `?${query}` : ''}`);
  },
  
  getProvider: (providerId) => apiFetch(`/providers/${providerId}`),
  
  getProviderByUser: (userId) => apiFetch(`/providers/user/${userId}`),
  
  createOrUpdateProfile: (data) => 
    apiFetch('/providers', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateAvailability: (availability) => 
    apiFetch('/providers/availability', {
      method: 'PUT',
      body: JSON.stringify(availability),
    }),
};

// Requests API
export const requestsAPI = {
  getRequests: (status = null) => {
    const query = status ? `?status=${status}` : '';
    return apiFetch(`/requests${query}`);
  },
  
  getRequestsInZone: () => apiFetch('/requests/provider-zone'),
  
  createRequest: (data) => 
    apiFetch('/requests', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateStatus: (requestId, status, price = null) => {
    const params = new URLSearchParams({ status });
    if (price !== null) params.append('price', price);
    return apiFetch(`/requests/${requestId}/status?${params}`, {
      method: 'PUT',
    });
  },
};

// Messages API
export const messagesAPI = {
  getMessages: (requestId) => apiFetch(`/messages/${requestId}`),
  
  sendMessage: (requestId, content) => 
    apiFetch('/messages', {
      method: 'POST',
      body: JSON.stringify({ request_id: requestId, content }),
    }),
};

// Reviews API
export const reviewsAPI = {
  getProviderReviews: (providerId) => apiFetch(`/reviews/${providerId}`),
  
  createReview: (data) => 
    apiFetch('/reviews', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Stats API
export const statsAPI = {
  getProviderStats: () => apiFetch('/stats/provider'),
  getProviderAnalytics: (period = '30') => apiFetch(`/stats/provider/analytics?period=${period}`),
};

// Notifications API
export const notificationsAPI = {
  getNotifications: () => apiFetch('/notifications'),
  
  markAsRead: (notificationId) => 
    apiFetch(`/notifications/${notificationId}/read`, { method: 'PUT' }),
  
  markAllAsRead: () => 
    apiFetch('/notifications/read-all', { method: 'PUT' }),
};

// Launch Offer API
export const launchOfferAPI = {
  getStatus: () => apiFetch('/launch-offer'),
};

// Matching API
export const matchingAPI = {
  getMatches: (city, serviceType, day, timeStart) => {
    let url = `/providers/match?city=${city}`;
    if (serviceType) url += `&service_type=${serviceType}`;
    if (day !== undefined && day !== null) url += `&day=${day}`;
    if (timeStart) url += `&time_start=${timeStart}`;
    return apiFetch(url);
  },
};

// Push Notifications API
export const pushAPI = {
  subscribe: (subscription) =>
    apiFetch('/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({ subscription }),
    }),
  unsubscribe: () => apiFetch('/push/subscribe', { method: 'DELETE' }),
};

export default {
  auth: authAPI,
  config: configAPI,
  pets: petsAPI,
  providers: providersAPI,
  requests: requestsAPI,
  messages: messagesAPI,
  reviews: reviewsAPI,
  stats: statsAPI,
  notifications: notificationsAPI,
  launchOffer: launchOfferAPI,
  matching: matchingAPI,
  push: pushAPI,
};
